import React, { useEffect, useState } from "react";
import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap";
import { defineMessages, useIntl } from "react-intl";
import { CommercialTermsRowChangeDto, CommercialTermsRowDto } from "./infrastructure/B2BAdminApi";

const messages = defineMessages({
  title: { id: "b2bConditions.editor.title", defaultMessage: "Edit commercial condition" },
  viewTitle: { id: "b2bConditions.editor.viewTitle", defaultMessage: "Commercial condition" },
  close: { id: "b2bConditions.editor.close", defaultMessage: "Close" },
  warning: {
    id: "b2bConditions.editor.warning",
    defaultMessage:
      "Master mode: the next Salesforce publication for this entity replaces this change.",
  },
  commission: { id: "b2bConditions.editor.commission", defaultMessage: "Commission (%)" },
  net: { id: "b2bConditions.editor.net", defaultMessage: "Net rate" },
  validFrom: { id: "b2bConditions.editor.validFrom", defaultMessage: "Valid from" },
  validTo: { id: "b2bConditions.editor.validTo", defaultMessage: "Valid to" },
  hotels: {
    id: "b2bConditions.editor.hotels",
    defaultMessage: "Properties (tickers, comma separated; empty for all)",
  },
  cancel: { id: "b2bConditions.editor.cancel", defaultMessage: "Cancel" },
  save: { id: "b2bConditions.editor.save", defaultMessage: "Save" },
  invalid: {
    id: "b2bConditions.editor.invalid",
    defaultMessage:
      "The commission must be between 0 and 100 and the end date cannot precede the start date.",
  },
});

interface B2BConditionEditorProps {
  row?: CommercialTermsRowDto;
  /** Consult only: the fields show and nothing can be saved. */
  readOnly?: boolean;
  saving: boolean;
  error?: string;
  onHide: () => void;
  onSave: (change: CommercialTermsRowChangeDto) => void;
}

interface FormState {
  commissionPct: string;
  net: boolean;
  validFrom: string;
  validTo: string;
  hotels: string;
}

const toForm = (row?: CommercialTermsRowDto): FormState => ({
  commissionPct: row?.commissionPct === undefined ? "" : String(row.commissionPct),
  net: row?.net ?? false,
  validFrom: row?.validFrom ?? "",
  validTo: row?.validTo ?? "",
  hotels: (row?.hotelCodes ?? []).join(", "),
});

/** Master mode only: edits what the listing shows of one row; the rest of the published line stays. */
export function B2BConditionEditor({
  row,
  readOnly = false,
  saving,
  error,
  onHide,
  onSave,
}: B2BConditionEditorProps) {
  const intl = useIntl();
  const [form, setForm] = useState<FormState>(toForm(row));
  const [invalid, setInvalid] = useState(false);
  const isLine = row?.kind === "line";

  useEffect(() => {
    setForm(toForm(row));
    setInvalid(false);
  }, [row]);

  const handleSave = () => {
    const pct =
      form.commissionPct.trim() === "" ? undefined : Number(form.commissionPct.replace(",", "."));
    const badPct = pct !== undefined && (Number.isNaN(pct) || pct < 0 || pct > 100);
    const badDates = isLine && form.validFrom && form.validTo && form.validTo < form.validFrom;
    if (badPct || badDates) {
      setInvalid(true);
      return;
    }
    onSave({
      commissionPct: pct,
      net: form.net,
      validFrom: form.validFrom || undefined,
      validTo: form.validTo || undefined,
      hotelCodes: form.hotels
        .split(",")
        .map(it => it.trim())
        .filter(Boolean),
    });
  };

  return (
    <Modal show={!!row} onHide={onHide}>
      <Modal.Header closeButton>
        <Modal.Title>
          {intl.formatMessage(readOnly ? messages.viewTitle : messages.title)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!readOnly && <Alert variant="warning">{intl.formatMessage(messages.warning)}</Alert>}
        {(invalid || error) && (
          <Alert variant="danger">{invalid ? intl.formatMessage(messages.invalid) : error}</Alert>
        )}
        <fieldset disabled={readOnly}>
          <Form.Group>
            <Form.Label>{intl.formatMessage(messages.commission)}</Form.Label>
            <Form.Control
              type="number"
              min={0}
              max={100}
              step="0.01"
              value={form.commissionPct}
              onChange={event => setForm({ ...form, commissionPct: event.target.value })}
            />
          </Form.Group>
          {isLine && (
            <>
              <Form.Group>
                <Form.Check
                  type="checkbox"
                  id="b2b-condition-net"
                  label={intl.formatMessage(messages.net)}
                  checked={form.net}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    setForm({ ...form, net: event.target.checked })
                  }
                />
              </Form.Group>
              <Row>
                <Col>
                  <Form.Group>
                    <Form.Label>{intl.formatMessage(messages.validFrom)}</Form.Label>
                    <Form.Control
                      type="date"
                      value={form.validFrom}
                      onChange={event => setForm({ ...form, validFrom: event.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group>
                    <Form.Label>{intl.formatMessage(messages.validTo)}</Form.Label>
                    <Form.Control
                      type="date"
                      value={form.validTo}
                      onChange={event => setForm({ ...form, validTo: event.target.value })}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group>
                <Form.Label>{intl.formatMessage(messages.hotels)}</Form.Label>
                <Form.Control
                  value={form.hotels}
                  onChange={event => setForm({ ...form, hotels: event.target.value })}
                />
              </Form.Group>
            </>
          )}
        </fieldset>
      </Modal.Body>
      <Modal.Footer>
        {readOnly ? (
          <Button variant="secondary" onClick={onHide}>
            {intl.formatMessage(messages.close)}
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onHide} disabled={saving}>
              {intl.formatMessage(messages.cancel)}
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {intl.formatMessage(messages.save)}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}
